"use client";

import React from "react";
import { Minus, Plus, Tag, Folder } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { Separator } from "@/components/ui/separator";
import {
  EcommerceProductsProvider,
  useEcommerceProducts,
  AddToCartButton,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";

declare global {
  interface Window {
    wvcClient: any;
  }
}

function SingleProductConsumer() {
  const { products, loading, error } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="aspect-square bg-card/40 animate-pulse" />
        <div className="space-y-6">
          <div className="h-10 w-3/4 bg-card/40 animate-pulse" />
          <div className="h-6 w-1/3 bg-card/40 animate-pulse" />
          <div className="h-24 w-full bg-card/40 animate-pulse" />
          <div className="h-12 w-full bg-card/40 animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-border bg-card/40 backdrop-blur-md p-8 text-center">
        <p className="text-muted-foreground">Unable to load product details.</p>
      </div>
    );
  }

  if (!products || products.length === 0) {
    return (
      <div className="border border-border bg-card/40 backdrop-blur-md p-8 text-center">
        <p className="text-muted-foreground">Product not found.</p>
      </div>
    );
  }

  const product = products[0];
  const hasDiscount =
    product.discounted_price !== undefined &&
    product.discounted_price !== null &&
    product.discounted_price > 0 &&
    product.discounted_price < product.price;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
      {}
      <div className="relative">
        <div className="absolute -inset-2 bg-primary/10 blur-2xl pointer-events-none" />
        <div
          className="relative border border-border bg-card/40 backdrop-blur-md overflow-hidden rounded-lg"
          style={{ boxShadow: "var(--shadow-3xl)" }}
        >
          {product.featured_image_url ? (
            <div className="aspect-square overflow-hidden bg-background">
              <img
                src={product.featured_image_url}
                alt={product.title}
                className="w-full h-full object-cover"
                loading="lazy"
                decoding="async"
              />
            </div>
          ) : (
            <div className="aspect-square flex items-center justify-center bg-card">
              <span className="text-muted-foreground font-mono text-xs uppercase tracking-widest">
                No Image
              </span>
            </div>
          )}
          {hasDiscount && (
            <div className="absolute top-4 left-4">
              <Badge className="bg-primary text-primary-foreground font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg">
                Sale
              </Badge>
            </div>
          )}
        </div>
      </div>

      {}
      <div className="flex flex-col">
        <div className="mb-4">
          <span className="font-default text-xs uppercase tracking-[0.25em] text-primary">
            B.Y.E. Hygiene Mist™
          </span>
        </div>

        <h1
          className="font-default font-bold text-4xl sm:text-5xl lg:text-6xl leading-[0.95] tracking-tight mb-6"
          style={{ fontFamily: "var(--font-default)" }}
        >
          {product.title}
        </h1>

        {}
        <div className="flex items-baseline gap-4 mb-8">
          {hasDiscount ? (
            <>
              <span className="font-default font-bold text-4xl text-primary">
                ${product.discounted_price?.toFixed(2)}
              </span>
              <span className="font-default text-2xl text-muted-foreground line-through">
                ${product.price.toFixed(2)}
              </span>
            </>
          ) : (
            <span className="font-default font-bold text-4xl text-primary">
              ${product.price?.toFixed(2)}
            </span>
          )}
        </div>

        {}
        {product.short_description && (
          <div
            className="text-base sm:text-lg leading-relaxed text-foreground/85 mb-8 [&_p]:mb-3 [&_p:last-child]:mb-0"
            style={{ fontFamily: "var(--font-default)" }}
            dangerouslySetInnerHTML={{ __html: product.short_description }}
          />
        )}

        <Separator className="bg-border mb-8" />

        {}
        <div className="mb-8">
          <AddToCartButton
            productId={product.id}
            className="w-full"
            buttonClassName="bg-primary text-primary-foreground rounded-lg"
            text="Add to Cart"
            showQuantity={true}
            size="lg"
          />
        </div>

        {}
        <div className="space-y-4">
          {product.categories && product.categories.length > 0 && (
            <div className="flex items-start gap-3">
              <div className="flex items-center gap-2 min-w-32">
                <Folder className="size-4 text-primary" />
                <span className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                  Category
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.categories.map((cat, i) => (
                  <Link
                    key={cat.id}
                    to={cat.link || "#"}
                    data-index={i}
                    className="font-default text-xs uppercase tracking-wider text-foreground hover:text-primary border border-border px-3 py-1 transition-colors rounded-lg"
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {product.tags && product.tags.length > 0 && (
            <div className="flex items-start gap-3">
              <div className="flex items-center gap-2 min-w-32">
                <Tag className="size-4 text-primary" />
                <span className="font-default text-xs uppercase tracking-widest text-muted-foreground">
                  Tags
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {product.tags.map((tag, i) => (
                  <Link
                    key={tag.id}
                    to={tag.link || "#"}
                    data-index={i}
                    className="font-default text-xs uppercase tracking-wider text-foreground hover:text-primary border border-border px-3 py-1 transition-colors rounded-lg"
                  >
                    #{tag.name}
                  </Link>
                ))}
              </div>
            </div>
          )}
        </div>

        {}
        <div className="mt-10 border border-border bg-card/40 backdrop-blur-md p-6 rounded-lg">
          <h3 className="font-default font-bold text-sm uppercase tracking-[0.25em] text-primary mb-4">
            Product Details
          </h3>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <dt className="font-default text-xs uppercase tracking-wider text-muted-foreground mb-1">
                Formula
              </dt>
              <dd className="font-default text-foreground">Gentle & Natural</dd>
            </div>
            <div>
              <dt className="font-default text-xs uppercase tracking-wider text-muted-foreground mb-1">
                Usage
              </dt>
              <dd className="font-default text-foreground">Before You Enter</dd>
            </div>
            <div>
              <dt className="font-default text-xs uppercase tracking-wider text-muted-foreground mb-1">
                Portability
              </dt>
              <dd className="font-default text-foreground">Travel-Friendly</dd>
            </div>
            <div>
              <dt className="font-default text-xs uppercase tracking-wider text-muted-foreground mb-1">
                Safety
              </dt>
              <dd className="font-default text-foreground">Family-Safe</dd>
            </div>
          </dl>
        </div>
      </div>
    </div>
  );
}

function FullDescriptionConsumer() {
  const { products, loading } = useEcommerceProducts();

  if (loading || !products || products.length === 0) return null;
  const product = products[0];
  if (!product.long_description) return null;

  return (
    <div className="mt-20 lg:mt-28">
      <div className="border-t border-border pt-12">
        <div className="flex items-center gap-4 mb-8">
          <span className="font-default text-xs uppercase tracking-[0.25em] text-primary">
            01 / Product Info
          </span>
          <div className="flex-1 h-px bg-border" />
        </div>
        <h2
          className="font-default font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight mb-8"
          style={{ fontFamily: "var(--font-default)" }}
        >
          Why Choose B.Y.E. Mist?
        </h2>
        <div
          className="max-w-3xl text-base sm:text-lg leading-relaxed text-foreground/85 [&_p]:mb-4 [&_p:last-child]:mb-0 [&_h2]:font-default [&_h2]:font-bold [&_h2]:text-2xl [&_h2]:mt-8 [&_h2]:mb-4 [&_h3]:font-default [&_h3]:font-bold [&_h3]:text-xl [&_h3]:mt-6 [&_h3]:mb-3 [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4 [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-4 [&_li]:mb-1 [&_a]:text-primary [&_a:hover]:underline [&_strong]:font-bold [&_strong]:text-foreground"
          style={{ fontFamily: "var(--font-default)" }}
          dangerouslySetInnerHTML={{ __html: product.long_description }}
        />
      </div>
    </div>
  );
}

export default function Product() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : {};

  return (
    <section data-section-id="4941"
      id="single-product-template-section"
      className="bg-background text-foreground relative overflow-hidden"
    >
      {}
      <div className="absolute inset-0 pointer-events-none opacity-[0.04]">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
      </div>

      {}
      <div className="absolute top-20 -right-32 size-96 bg-primary/20 blur-[120px] rounded-lg pointer-events-none" />
      <div className="absolute bottom-20 -left-32 size-96 bg-primary/10 blur-[120px] rounded-lg pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-28">
        {}
        <nav className="flex items-center gap-2 mb-12 font-default text-xs uppercase tracking-widest text-muted-foreground">
          <Link to="/BYEHygieneMistHomePage" className="text-foreground hover:text-primary transition-colors">
            Home
          </Link>
          <span>/</span>
          <Link
            to="/ShopPage"
            className="text-foreground hover:text-primary transition-colors"
          >
            Shop
          </Link>
          <span>/</span>
          <span className="text-primary">Product</span>
        </nav>

        <EcommerceProductsProvider wp_query={currentWPQueryParams}>
          <SingleProductConsumer />
          <FullDescriptionConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}